/**
 * Tâche 1438 (Eric 2026-10-02) — « Garde la trace des fichiers refusés pour
 * qu'on voie les vraies causes au lieu de les deviner ».
 *
 * Le panneau montre d'abord **le compte par ÉTAPE**, car c'est l'étape qui dit
 * où corriger : un refus prononcé par le navigateur (extension hors liste), par
 * le garde du serveur, par l'extracteur ou par le seuil des 30 caractères ne
 * demandent pas le même travail. Vient ensuite la liste, avec l'empreinte
 * technique du fichier — c'est elle qui aurait donné la cause du PDF d'Eric en
 * une minute (texte rangé dans neuf annotations) au lieu de trois diagnostics
 * faux.
 *
 * ⚠️ Le CONTENU des fichiers refusés n'est jamais conservé (loi 25) : le
 * panneau ne peut donc rien afficher d'autre que des caractéristiques.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CaretDown, CaretRight, SpinnerGap, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API_BASE as API } from "../../lib/apiBase";
import { confirmDialog } from "../../lib/uiDialogs";
import { useLang } from "../../contexts/LanguageContext";

const LIBELLE_ETAPE = {
  navigateur: ["Refusé par l'écran (avant envoi)", "Rejected by the screen (before upload)"],
  garde_serveur: ["Refusé par le serveur (format)", "Rejected by the server (format)"],
  extracteur: ["Lecture du fichier impossible", "File could not be read"],
  seuil_30: ["Aucun texte exploitable (< 30 caractères)", "No usable text (< 30 characters)"],
  taille: ["Fichier trop volumineux", "File too large"],
  vide: ["Fichier vide", "Empty file"],
  inconnue: ["Étape inconnue", "Unknown step"],
};

// Tâche 1451 — les durées proposées par Eric, dans son ordre. « jamais » est
// le défaut : c'est ce qu'il a demandé explicitement, contre les 90 jours qui
// effaçaient tout seuls.
const CONSERVATIONS = [
  ["jamais", "jamais", "never"],
  ["1_semaine", "plus vieux qu'une semaine", "older than a week"],
  ["1_mois", "plus vieux qu'un mois", "older than a month"],
  ["3_mois", "plus vieux que trois mois", "older than three months"],
  ["1_an", "plus vieux qu'un an", "older than a year"],
];

export const ImportRefusalsPanel = ({ kind = "document" }) => {
  const { lang, tr } = useLang();
  const [ouvert, setOuvert] = useState(false);
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axios.get(
        `${API}/api/import-refusals?limit=50&kind=${kind}`,
        { withCredentials: true });
      setDonnees(data);
    } catch {
      setDonnees({ refus: [], total: 0, par_etape: {}, par_extension: {},
                   conservation: "jamais" });
    } finally {
      setChargement(false);
    }
  }, [kind]);

  useEffect(() => { if (ouvert && !donnees) charger(); }, [ouvert, donnees, charger]);

  /** Tâche 1451 — ⚠️ Le MENU règle la conservation à venir, le BOUTON efface
      maintenant. Mêler les deux ferait d'un ménage ponctuel une règle
      permanente, ou l'inverse : deux gestes, deux effets. */
  const changerConservation = async (duree) => {
    setOccupe(true);
    try {
      const { data } = await axios.post(`${API}/api/import-refusals/retention`,
        { kind, duree }, { withCredentials: true });
      if (data.efface) {
        toast.success(tr(`${data.efface} refus effacé(s) par ce réglage.`,
                         `${data.efface} rejection(s) deleted by this setting.`));
      }
      await charger();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setOccupe(false);
    }
  };

  const effacerMaintenant = async () => {
    const duree = donnees?.conservation || "jamais";
    if (duree === "jamais") {
      toast.info(tr("Choisissez d'abord une ancienneté : « jamais » n'efface rien.",
                    "Pick an age first: \"never\" deletes nothing."));
      return;
    }
    const libelle = (CONSERVATIONS.find((c) => c[0] === duree) || [])[lang === "fr" ? 1 : 2];
    const ok = await confirmDialog(
      tr(`Effacer définitivement les refus ${libelle} ?`,
         `Permanently delete rejections ${libelle}?`),
      { danger: true, confirmLabel: tr("Effacer", "Delete") });
    if (!ok) return;
    setOccupe(true);
    try {
      const { data } = await axios.post(`${API}/api/import-refusals/purge`,
        { kind, duree }, { withCredentials: true });
      toast.success(tr(`${data.efface} refus effacé(s).`,
                       `${data.efface} rejection(s) deleted.`));
      await charger();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setOccupe(false);
    }
  };

  const etapes = Object.entries(donnees?.par_etape || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="mb-4 border border-zinc-200 bg-white rounded-xl overflow-hidden"
         data-testid="import-refusals-panel">
      <button
        type="button"
        onClick={() => setOuvert((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors"
        data-testid="import-refusals-toggle"
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-800">
          {ouvert ? <CaretDown size={14} /> : <CaretRight size={14} />}
          <Warning size={16} className="text-amber-600" weight="duotone" />
          {kind === "video"
            ? tr("Vidéos refusées à l'import", "Videos rejected at import")
            : tr("Fichiers refusés à l'import", "Files rejected at import")}
        </span>
        <span className="text-[11px] text-zinc-500">
          {donnees
            ? (donnees.conservation === "jamais"
              ? tr(`${donnees.total} refus · conservés sans limite`,
                   `${donnees.total} rejection(s) · kept indefinitely`)
              : tr(`${donnees.total} refus · effacés quand ils sont ${(CONSERVATIONS.find((c) => c[0] === donnees.conservation) || [])[1]}`,
                   `${donnees.total} rejection(s) · deleted when ${(CONSERVATIONS.find((c) => c[0] === donnees.conservation) || [])[2]}`))
            : tr("Voir les vraies causes", "See the real causes")}
        </span>
      </button>

      {ouvert && (
        <div className="p-4 border-t border-zinc-200 space-y-3">
          {chargement && (
            <p className="text-xs text-zinc-500 inline-flex items-center gap-2">
              <SpinnerGap size={14} className="animate-spin" />
              {tr("Chargement…", "Loading…")}
            </p>
          )}
          {donnees && donnees.total === 0 && (
            <p className="text-xs text-zinc-500" data-testid="import-refusals-empty">
              {tr("Aucun fichier refusé pour l'instant. Les refus futurs seront conservés ici, avec l'étape qui a refusé et les caractéristiques du fichier (jamais son contenu).",
                  "No rejected file yet. Future rejections will be kept here, with the step that rejected and the file characteristics (never its content).")}
            </p>
          )}
          {etapes.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="import-refusals-by-step">
              {etapes.map(([etape, n]) => (
                <span key={etape}
                      className="text-[11px] px-2 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-900 font-semibold">
                  {(LIBELLE_ETAPE[etape] || [etape, etape])[lang === "fr" ? 0 : 1]} · {n}
                </span>
              ))}
            </div>
          )}
          {donnees?.refus?.length > 0 && (
            <ul className="divide-y divide-zinc-100 max-h-72 overflow-y-auto border border-zinc-200 rounded-lg"
                data-testid="import-refusals-list">
              {donnees.refus.map((r, i) => (
                <li key={i} className="px-3 py-2" data-testid={`import-refusal-${i}`}>
                  <p className="text-xs font-semibold text-zinc-800 break-all">{r.filename}</p>
                  <p className="text-[11px] text-amber-900">
                    {(LIBELLE_ETAPE[r.etape] || [r.etape, r.etape])[lang === "fr" ? 0 : 1]}
                    {" · "}{r.message}
                  </p>
                  <p className="text-[10px] text-zinc-500 break-all">
                    {r.extension} · {Math.round((r.size_bytes || 0) / 1024)} Ko
                    {r.empreinte && Object.keys(r.empreinte).length > 1 && (
                      <> · {JSON.stringify(r.empreinte)}</>
                    )}
                  </p>
                  <p className="text-[10px] text-zinc-400">{r.created_at} · {r.user_email}</p>
                </li>
              ))}
            </ul>
          )}
          {/* Tâche 1451 — le réglage de conservation et l'effacement. */}
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-100">
            <label className="text-[11px] text-zinc-500" htmlFor={`conservation-${kind}`}>
              {tr("Effacer les refus :", "Delete rejections:")}
            </label>
            <select
              id={`conservation-${kind}`}
              value={donnees?.conservation || "jamais"}
              disabled={occupe}
              onChange={(e) => changerConservation(e.target.value)}
              className="px-2 py-1 border border-zinc-300 rounded text-[11px] bg-white disabled:opacity-50"
              data-testid={`import-refusals-retention-${kind}`}
            >
              {CONSERVATIONS.map(([valeur, fr, en]) => (
                <option key={valeur} value={valeur}>{lang === "fr" ? fr : en}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={effacerMaintenant}
              disabled={occupe}
              className="text-[11px] font-semibold px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
              data-testid={`import-refusals-purge-${kind}`}
              title={tr("Efface tout de suite les refus de cette ancienneté. Ne change pas le réglage.",
                        "Deletes rejections of that age right now. Does not change the setting.")}
            >
              {tr("Effacer maintenant", "Delete now")}
            </button>
            <button
              type="button"
              onClick={charger}
              className="text-[11px] text-[#002FA7] hover:underline font-semibold ml-auto"
              data-testid="import-refusals-reload"
            >
              {tr("Recharger", "Reload")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportRefusalsPanel;
